import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { WALLET_QUERY_KEYS } from '../../../wallet/api.js';
import { holdWalletBalance, whenWalletBalanceUnheld } from '../../../wallet/balanceHold.js';
import { invalidateClaimState } from '../../../promo/api.js';
import { mapPlayError } from '../../playError.js';
import { outcomeUnknown, playUntilKnown, useUnmountSignal } from '../../playUntilKnown.js';
import type { ParamValue, PlayEnvelope } from '../../types.js';

/**
 * A single resolved Plinko drop, handed to the scene to animate. Unlike the shared
 * single-flight `useOriginalsPlay`, Plinko fires many of these concurrently — each is an
 * independent provably-fair server round with its own path / bucket.
 */
export interface PlinkoDrop {
  id: string;
  path: ('L' | 'R')[];
  bucket: number;
  /** Bucket multiplier in basis points, mirrored from the resolved config for impact colouring. */
  multiplierBp: number;
  envelope: PlayEnvelope;
}

interface PlinkoPresentation {
  path: ('L' | 'R')[];
  bucket: number;
}

export interface UsePlinkoDrops {
  /** Number of server rounds awaiting their response (not yet animating). */
  pendingCount: number;
  error: string | null;
  /**
   * Fire one drop. Resolves with the {@link PlinkoDrop} once the server round returns,
   * or null on error (error is surfaced via {@link UsePlinkoDrops.error}). Never blocks
   * the next call — spam-clicking spawns concurrent rounds.
   */
  drop: (betAmount: bigint, params: Record<string, ParamValue>) => Promise<PlinkoDrop | null>;
  /** Called by the scene when a drop's ball settles into its bucket — refreshes balance. */
  onSettled: (dropId: string) => void;
  clearError: () => void;
}

function readMultiplierBp(buckets: number[] | undefined, bucket: number): number {
  if (!buckets) return 0;
  const v = buckets[bucket];
  return typeof v === 'number' ? v : 0;
}

/**
 * Plinko-only concurrent-drop driver. Each call to {@link UsePlinkoDrops.drop} POSTs an
 * independent originals round (unique idempotency key) WITHOUT a global in-flight lock, so
 * the Bet button stays live and the player can stack many balls. Wallet balance is
 * invalidated per settle (via {@link UsePlinkoDrops.onSettled}), not per request, so the
 * displayed balance tracks the visual landings rather than the network responses.
 */
export function usePlinkoDrops(
  gameCode: string,
  presenterBuckets: () => number[] | undefined,
): UsePlinkoDrops {
  const { t } = useTranslation('originals');
  const queryClient = useQueryClient();
  const [pendingCount, setPendingCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const bucketsRef = useRef(presenterBuckets);
  bucketsRef.current = presenterBuckets;
  const unmountSignal = useUnmountSignal();
  // The wallet books each drop the moment it is placed and the SSE stream pushes
  // that balance while the ball is still falling: every ball in the air holds
  // the shown balance until it lands.
  const balanceHolds = useRef(new Map<string, () => void>());
  useEffect(() => {
    const holds = balanceHolds.current;
    return () => {
      for (const release of holds.values()) release();
      holds.clear();
    };
  }, []);

  const drop = useCallback(
    async (betAmount: bigint, params: Record<string, ParamValue>): Promise<PlinkoDrop | null> => {
      setError(null);
      setPendingCount((n) => n + 1);
      let confirming = false;
      const releaseBalance = holdWalletBalance();
      try {
        // One key per drop, kept across every resend of it (see playUntilKnown).
        const envelope = await playUntilKnown(
          {
            gameCode,
            betAmount: Number(betAmount),
            params: params as Record<string, string | number>,
            idempotencyKey: crypto.randomUUID(),
          },
          {
            signal: unmountSignal.current,
            onConfirming: () => {
              confirming = true;
              setError(t('ORIG-020', { ns: 'errors' }));
            },
          },
        );
        // Clear only the note this drop put up; another drop's error stands.
        if (confirming) setError(null);
        const pres = envelope.outcome.presentation as PlinkoPresentation;
        const bucket = pres.bucket ?? 0;
        balanceHolds.current.set(envelope.roundId, releaseBalance);
        return {
          id: envelope.roundId,
          path: pres.path ?? [],
          bucket,
          multiplierBp: readMultiplierBp(bucketsRef.current(), bucket),
          envelope,
        };
      } catch (err) {
        releaseBalance();
        const mapped = mapPlayError(err);
        setError(t(mapped.key, { ns: mapped.ns }));
        if (outcomeUnknown(err)) void queryClient.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.balances });
        return null;
      } finally {
        setPendingCount((n) => Math.max(0, n - 1));
      }
    },
    [gameCode, t, queryClient],
  );

  const onSettled = useCallback((dropId: string) => {
    balanceHolds.current.get(dropId)?.();
    balanceHolds.current.delete(dropId);
    // A re-read would bring in the balls still falling, so it waits for the last one.
    whenWalletBalanceUnheld('refetch', () => void queryClient.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.balances }));
    // Drops count toward active bonus turnover — keep the Promo page fresh.
    invalidateClaimState(queryClient);
  }, [queryClient]);

  const clearError = useCallback(() => setError(null), []);

  return { pendingCount, error, drop, onSettled, clearError };
}
